import { useConfigStore, DEFAULT_CONFIG } from '../../stores/configStore';

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  defaultValue,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  defaultValue: number;
}) {
  const isModified = Math.abs(value - defaultValue) > 0.001;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-sm text-gray-600">{label}</label>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-sm ${isModified ? 'text-pebbl-600 font-medium' : 'text-gray-700'}`}>
            {value.toFixed(2)}
          </span>
          {isModified && (
            <span className="text-xs text-gray-400">({defaultValue})</span>
          )}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pebbl-600"
      />
    </div>
  );
}

export default function ConfigPanel() {
  const { config, setGate, setWeight, setFusionAlpha, setRerankTopM } = useConfigStore();

  return (
    <div className="card space-y-6">
      <h3 className="font-semibold text-gray-900">Configuration</h3>

      {/* Gates */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Gate Thresholds</h4>
        <div className="space-y-4">
          <Slider
            label="Min Overall Score"
            value={config.minScore}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => setGate('minScore', v)}
            defaultValue={DEFAULT_CONFIG.minScore}
          />
          <Slider
            label="Min Event"
            value={config.minEvent}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => setGate('minEvent', v)}
            defaultValue={DEFAULT_CONFIG.minEvent}
          />
          <Slider
            label="Min Location"
            value={config.minLocation}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => setGate('minLocation', v)}
            defaultValue={DEFAULT_CONFIG.minLocation}
          />
          <Slider
            label="Min Time"
            value={config.minTime}
            min={0}
            max={1}
            step={0.05}
            onChange={(v) => setGate('minTime', v)}
            defaultValue={DEFAULT_CONFIG.minTime}
          />
        </div>
      </div>

      {/* Weights */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Score Weights</h4>
        <div className="space-y-4">
          <Slider
            label="Event"
            value={config.weights.event}
            min={0}
            max={0.5}
            step={0.05}
            onChange={(v) => setWeight('event', v)}
            defaultValue={DEFAULT_CONFIG.weights.event}
          />
          <Slider
            label="Location"
            value={config.weights.location}
            min={0}
            max={0.5}
            step={0.05}
            onChange={(v) => setWeight('location', v)}
            defaultValue={DEFAULT_CONFIG.weights.location}
          />
          <Slider
            label="Time"
            value={config.weights.time}
            min={0}
            max={0.5}
            step={0.05}
            onChange={(v) => setWeight('time', v)}
            defaultValue={DEFAULT_CONFIG.weights.time}
          />
          <Slider
            label="Appearance"
            value={config.weights.appearance}
            min={0}
            max={0.5}
            step={0.05}
            onChange={(v) => setWeight('appearance', v)}
            defaultValue={DEFAULT_CONFIG.weights.appearance}
          />
          <Slider
            label="Habits"
            value={config.weights.habits}
            min={0}
            max={0.5}
            step={0.05}
            onChange={(v) => setWeight('habits', v)}
            defaultValue={DEFAULT_CONFIG.weights.habits}
          />
          <Slider
            label="Mirror Consistency"
            value={config.weights.mirrorConsistency}
            min={0}
            max={0.5}
            step={0.05}
            onChange={(v) => setWeight('mirrorConsistency', v)}
            defaultValue={DEFAULT_CONFIG.weights.mirrorConsistency}
          />
          <Slider
            label="Uniqueness Bonus"
            value={config.weights.uniquenessBonus}
            min={0}
            max={0.5}
            step={0.05}
            onChange={(v) => setWeight('uniquenessBonus', v)}
            defaultValue={DEFAULT_CONFIG.weights.uniquenessBonus}
          />
        </div>
      </div>

      {/* Fusion */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Fusion Settings</h4>
        <div className="space-y-4">
          <Slider
            label="Fusion Alpha (det vs LLM)"
            value={config.fusionAlpha}
            min={0}
            max={1}
            step={0.05}
            onChange={setFusionAlpha}
            defaultValue={DEFAULT_CONFIG.fusionAlpha}
          />
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600">Rerank Top M</label>
              <span className="font-mono text-sm text-gray-700">{config.rerankTopM}</span>
            </div>
            <input
              type="range"
              min={1}
              max={30}
              step={1}
              value={config.rerankTopM}
              onChange={(e) => setRerankTopM(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-pebbl-600"
            />
          </div>
        </div>
      </div>

      {/* Weight Sum Info */}
      <div className="pt-4 border-t text-xs text-gray-500">
        <div className="flex justify-between">
          <span>Weight sum:</span>
          <span className="font-mono">
            {Object.values(config.weights).reduce((a, b) => a + b, 0).toFixed(2)}
          </span>
        </div>
        <div className="text-gray-400 mt-1">
          Weights should sum to 1.0 for best results
        </div>
      </div>
    </div>
  );
}
